import React, { useCallback, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import Search from '../../shared/components/ui/Search';
import Table from '../../shared/components/layout/Table';

import { transformData } from '../../super-admin/utils/transformData';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getAllBlocks,
  handleCheckClosedDealsDelete,
  deleteClosedDeals,
  createClosedDeal,
  getLeadsForPicker,
  getLeadByID,
  cancelCloseDeal,
  getCloseDealsByFilters
} from '../services/api/implementation/impl';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  buildReturnToFromLocation,
  navigateWithReturn
} from '../../shared/navigation';
import { useForm } from 'react-hook-form';
import Cookies from 'js-cookie';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import NoDataFound from '../components/layout/NoDataFound';
import PaginationComponent from '../../shared/components/PaginationComponent';
import Modal from '../../shared/components/ui/Modal';
import UpdateSalesUser from '../components/forms/UpdateSalesUser';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import FormInput from '../../shared/components/ui/FormInput';
import DeleteConfirmationModal from '../components/ui/DeleteConfirmationModal';
import Button from '../../shared/components/ui/Button';
import Loader from '../../shared/components/ui/Loader';
import toast from 'react-hot-toast';
import { ClosedWonCloseDeal } from '../components/forms/ClosedWonCloseDeal';
import Headroom from 'react-headroom';
import mixpanel from 'mixpanel-browser';
import { useSelector, useDispatch } from 'react-redux';
import { multipleUploadFiles, uploadFile } from '../features/files';
import editIcon from '../../shared/assets/svg/edit.svg';
import deleteIcon from '../../shared/assets/svg/delete.svg';
import cancelDealIcon from '../../shared/assets/svg/cancelDeal.svg';
import TableData from '../../shared/components/ui/TableData';
import { ChevronDown } from 'lucide-react';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const CustomerList = ({ forcedProjectID = null }) => {
  const { id: projectId } = useParams();
  const {
    register,
    watch,
    formState: { errors: createErrors, errors: selectErrors },
    setValue,
    reset
  } = useForm({
    defaultValues: {
      lead_id: ''
    }
  });
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed } = useSidebar();
  const [projectID, setProjectID] = useState(null);
  const userId = Cookies.get('userId');
  const userDesignationLevel = Cookies.get('userDesignationLevel');
  const dispatch = useDispatch();
  const fileUrls = useSelector((state) => state.file.fileUrls);
  const formatFiles = (files) => {
    if (!Array.isArray(files) || files.length === 0) {
      return [];
    }
    return files.map((file) => ({
      name: file.name || 'default_name',
      url: file.url || file
    }));
  };
  // Track page view
  useEffect(() => {
    mixpanel.track('Customer List Viewed', {
      user_id: userId,
      project_id: projectId,
      timestamp: new Date().toISOString()
    });
  }, [userId, projectId]);

  const { data: blocksDyna, refetch: refetchBlocks } = useQuery({
    queryKey: ['blocks', projectId],
    queryFn: () => getAllBlocks(projectId),
    enabled: !!projectId
  });

  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulk, setIsBulk] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSelectLeadModalOpen, setIsSelectLeadModalOpen] = useState(false);
  const [leadPickerSearch, setLeadPickerSearch] = useState('');
  const [debouncedLeadSearch, setDebouncedLeadSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCloseDealModalOpen, setIsCloseDealModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [userVertical, setUserVertical] = useState(Cookies.get('userVertical'));
  const canAssignLead = userVertical !== 'Sales' || userDesignationLevel !== 'Low';

  const {
    data: leadsData,
    isLoading: isLeadsLoading,
    refetch: refetchLeads
  } = useQuery({
    queryKey: ['leadsPicker', 'deal', debouncedLeadSearch, projectID],
    queryFn: () =>
      getLeadsForPicker({
        page: 1,
        limit: 100,
        search: debouncedLeadSearch,
        excludeDeal: true,
        excludeUc: false,
        projectId: projectID || null
      }),
    enabled: isSelectLeadModalOpen,
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (!isSelectLeadModalOpen) {
      setLeadPickerSearch('');
      setDebouncedLeadSearch('');
      return;
    }
    const t = setTimeout(() => setDebouncedLeadSearch(leadPickerSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [leadPickerSearch, isSelectLeadModalOpen]);

  const [activeFilters, setActiveFilters] = useState(() => {
    // Restore project only. Never restore approval status — a sticky "Approved"
    // filter hides Pending deals (management-approval in progress) from sales.
    const savedFilters = localStorage.getItem('customerListFilters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        return {
          project: parsed.project ?? null,
          projectId: parsed.projectId ?? null,
          status: 'All'
        };
      } catch {
        // fall through
      }
    }
    return {
      project: null,
      projectId: null,
      status: 'All'
    };
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [leadID, setLeadID] = useState();
  const [ucDetails, setUcDetails] = useState({
    project: null,
    block: null,
    level: null,
    unit: null
  });
  // Table header filter states
  const [openDropdown, setOpenDropdown] = useState(null);
  const projectHeaderRef = useRef(null);
  const statusHeaderRef = useRef(null);
  const [projectDropdownRect, setProjectDropdownRect] = useState(null);
  const [statusDropdownRect, setStatusDropdownRect] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);

  // pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // items per page

  // Status filter options
  const statusOptions = ['All', 'Pending', 'Approved', 'Rejected', 'Cancelled'];

  const {
    data: deals,
    refetch: refetchDeals,
    isFetching
  } = useQuery({
    queryKey: ['deals', page, projectID, searchQuery, activeFilters.status],
    queryFn: () =>
      getCloseDealsByFilters(projectID, page, limit, searchQuery, activeFilters.status),
    keepPreviousData: true
  });

  useEffect(() => {
    refetchDeals();
  }, []);

  // Refetch data when filters change
  useEffect(() => {
    if (projectID) {
      refetchDeals();
    }
  }, [activeFilters.status, activeFilters.projectId]);

  // Check localStorage first, then default to first project
  React.useEffect(() => {
    if (projects && isInitialLoad) {
      if (forcedProjectID) {
        const forcedProject = projects.find((p) => String(p.id) === String(forcedProjectID));
        setProjectID(forcedProjectID);
        setActiveFilters((prev) => ({
          ...prev,
          project: forcedProject?.name || prev.project || null,
          projectId: forcedProjectID
        }));
        setIsInitialLoad(false);
        return;
      }

      // Only restore filters on initial load
      if (activeFilters.project && activeFilters.projectId) {
        // Restore project ID from saved filters
        setProjectID(activeFilters.projectId);
      } else {
        setProjectID(null);
        setActiveFilters((prev) => ({ ...prev, project: null, projectId: null }));
        saveFiltersToStorage({ ...activeFilters, project: null, projectId: null });
      }
      setIsInitialLoad(false);
    }
  }, [projects, isInitialLoad, forcedProjectID]);

  console.log({ deals });

  const totalPages = deals?.meta ? Math.ceil(deals?.meta.total / limit) : 1;

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const [newBlockId, setNewBlockId] = useState(null);
  const [blocksDynaWithLevels, setBlocksDynaWithLevels] = useState([]);
  const CustomerFormat = [
    'id',
    'name',
    'phone',
    'email',
    'project',
    'unit_sold',
    'Plc_Price',
    'cost',
    'GST',
    'total',
    'status',
    'actions',
    'lead_id'
  ];

  const formatAmount = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '0';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleClosedCustomerData = (item) => {
    return {
      id: item?.id,
      name: item?.lead?.user?.name || '---',
      phone: item?.lead?.user?.phone_number || '---',
      project: item?.unit?.project?.name || '---',
      unit_sold: `${item?.unit?.block?.name || ''} ${item?.unit?.unitName || ''}`.trim() || '---',
      Plc_Price: `₹ ${formatAmount(item?.plc_price || 0)}`,
      cost: item?.unit_price === 0 ? '0' : item?.unit_price,
      GST: `₹ ${formatAmount(item?.gst_price || 0)}`,
      total: `₹ ${formatAmount(item?.total_price || 0)}`,
      actions: `Actions---${item?.id}---${item?.lead?.sales_id || ''}`,
      status: item?.status || '---',
      lead_id: item?.lead?.id
    };
  };

  console.log({ deals });

  // Filter the deals based on sales_id
  // const filteredDeals = deals?.filter(deal => deal.lead?.sales_id === parseInt(userId));
  // console.log({ filteredDeals });

  // Transform the filtered deals

  useEffect(() => {
    setFilteredData(CustomerData);
  }, [deals?.data]);

  const CustomerData = deals?.data?.map((item) =>
    transformData(handleClosedCustomerData(item), CustomerFormat)
  );

  console.log({ CustomerData });

  const handleViewClosedDealsByID = (id) => {
    const deal = deals?.data?.find((deal) => deal.id === parseInt(id));
    navigateWithReturn(navigate, `/builder/deals/${deal?.lead?.id}`, {
      returnTo: buildReturnToFromLocation(location),
      state: { status: 'Closed Won' }
    });
  };

  const handleSelectBlockChange = (event) => {
    const selectedBlockId = event.target.value;
    setNewBlockId(selectedBlockId);
  };

  useEffect(() => {
    if (blocksDyna?.blocks) {
      const blocksWithLevels = blocksDyna.blocks.filter((block) => block?.levels?.length > 0);
      const sorted = blocksWithLevels.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      if (sorted.length > 0) {
        setBlocksDynaWithLevels(sorted);
        setNewBlockId(sorted[0].id);
      }
    }
  }, [blocksDyna]);

  const handleOnClickSearch = async () => {
    await refetchLeads();
  };

  const handleSearchChange = useCallback(
    async (incomingQuery) => {
      setPage(1);
      setSearchQuery(incomingQuery);

      // Track search
      mixpanel.track('Closed deal List Search', {
        user_id: userId,
        search_query: incomingQuery,
        project_id: projectID,
        timestamp: new Date().toISOString()
      });
    },
    [userId, projectID]
  );

  const handleStatusFilterChange = (status) => {
    setActiveFilters((prev) => ({ ...prev, status }));
    setPage(1); // Reset to first page when filter changes
    saveFiltersToStorage({ ...activeFilters, status });

    // Track status filter change
    mixpanel.track('Customer List Status Filter Changed', {
      user_id: userId,
      status: status,
      project_id: projectID,
      timestamp: new Date().toISOString()
    });
  };

  // Close table header dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openDropdown &&
        !event.target.closest('th') &&
        !event.target.closest('[data-portal-dropdown]')
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // PortalDropdown component for table header filters
  const PortalDropdown = ({ anchorRect, children, onClose }) => {
    if (!anchorRect) return null;
    return createPortal(
      <div
        data-portal-dropdown
        className="absolute bg-white border rounded shadow z-[1000]"
        style={{
          position: 'fixed',
          left: anchorRect.left,
          top: anchorRect.bottom,
          minWidth: anchorRect.width,
          maxHeight: '250px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}>
        {children}
      </div>,
      document.body
    );
  };

  // Handler to open dropdown and set position
  const handleHeaderDropdownClick = (type, event) => {
    // If clicking the same dropdown that's already open, close it
    if (openDropdown === type) {
      setOpenDropdown(null);
      return;
    }

    if (type === 'project' && projectHeaderRef?.current) {
      setProjectDropdownRect(projectHeaderRef?.current?.getBoundingClientRect());
    }
    if (type === 'status' && statusHeaderRef?.current) {
      setStatusDropdownRect(statusHeaderRef?.current?.getBoundingClientRect());
    }
    setOpenDropdown(type);
  };

  // Handler to close dropdown
  const handleCloseDropdown = () => setOpenDropdown(null);

  // Handler to select item from table header dropdown
  const handleSelectDropdownItem = (type, value) => {
    setOpenDropdown(null);

    if (type === 'project') {
      if (!value) {
        // Clear project filter
        mixpanel.track('Customer List Filter Cleared', {
          user_id: userId,
          filter_type: 'project',
          timestamp: new Date().toISOString()
        });
        setProjectID(null);
        const newFilters = { ...activeFilters, project: null };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      } else {
        mixpanel.track('Customer List Filter Changed', {
          user_id: userId,
          filter_type: 'project',
          project_id: value,
          timestamp: new Date().toISOString()
        });

        // Update active filters with project name and ID
        const selectedProject = projectsForDropdown?.find((p) => p.id === value);
        const newFilters = {
          ...activeFilters,
          project: selectedProject?.label || null,
          projectId: value
        };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);

        // Set project ID for API calls
        setProjectID(value);
      }
      setPage(1); // Reset to first page when project filter changes
    } else if (type === 'status') {
      if (!value) {
        // Clear status filter
        mixpanel.track('Customer List Filter Changed', {
          user_id: userId,
          filter_type: 'status',
          status: 'All',
          timestamp: new Date().toISOString()
        });
        const newFilters = { ...activeFilters, status: 'All' };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      } else {
        mixpanel.track('Customer List Filter Changed', {
          user_id: userId,
          filter_type: 'status',
          status: value,
          timestamp: new Date().toISOString()
        });
        const newFilters = { ...activeFilters, status: value };
        setActiveFilters(newFilters);
        saveFiltersToStorage(newFilters);
      }
      setPage(1); // Reset to first page when status filter changes
    }
  };

  const handleUpdate = (id) => {
    const deal = deals?.data?.find((deal) => deal.id === parseInt(id));
    setSelectedLead({ id: deal.lead.id, salesId: deal.lead.sales_id });
    setIsUpdateModalOpen(true);
  };

  const { mutate: handleCheckDelete } = useMutation({
    mutationFn: (id) => handleCheckClosedDealsDelete(id),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async (res, id) => {
      setIsSubmitting(false);
      const closedDeals = deals?.data?.find((item) => parseInt(item.id) === parseInt(id));
      const closedDealsName = closedDeals?.lead?.user?.name || 'Closed Deals';
      if (res.canDelete) {
        setCanDelete('yes');
        setResData({ name: closedDealsName });
        setSelectedIds([parseInt(id)]);
        setIsSubmitting(false);
      } else {
        // Fallback if not found
        setResData({ ...res, name: closedDealsName });
        setCanDelete('no');
        setIsSubmitting(false);
        setSelectedIds([parseInt(id)]);
      }
    },
    onError: (err) => {
      setIsSubmitting(false);
      setIsSubmitting(false);
      toast.error(err.response.data.message || err?.message || 'Error checking dependencies');
    }
  });

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (payload) => deleteClosedDeals(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Closed Deals Deleted Successfully');
      setSelectedIds([]);
      setSelectedItems([]);
      setCanDelete(null);
      setIsBulk(false);
      setSelectAll(false);
      refetchDeals();
    },
    onError: (err) => {
      setIsSubmitting(false);
      toast.error(err.response.data.message || err?.message || 'Error deleting closed deals');
    }
  });

  const HandleDelete = (data) => {
    const payload = { ids: data };
    handleDeleteFn(payload);
  };

  const handleSelectAll = async (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);

    if (checked) {
      setIsSubmitting(true); // Start loader

      try {
        const promises = deals?.data?.map((item) =>
          handleCheckClosedDealsDelete(item.id)
            .then((res) => {
              if (res.canDelete) return item.id;
              return null;
            })
            .catch(() => null)
        );

        const results = await Promise.all(promises);
        const validIds = results.filter((id) => id !== null);
        setSelectedItems(validIds);
      } finally {
        setIsSubmitting(false); // Stop loader
      }
    } else {
      setSelectedItems([]);
    }
  };

  const HandleFunctionCalls = async (id, checked) => {
    if (checked) {
      setIsSubmitting(true);
      try {
        const res = await handleCheckClosedDealsDelete(id);
        if (res.canDelete) {
          setIsSubmitting(false);
          setSelectedItems((prev) => [...prev, id]);
        } else if (!res.canDelete) {
          setIsSubmitting(false);
          toast.error(`Can not delete this closed deals because there is dependencies`);
        }
      } catch (err) {
        setIsSubmitting(false);
        toast.error(err.response.data.message || err?.message || 'Error checking dependencies');
      }
    } else {
      setIsSubmitting(false);
      setSelectedItems((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleCloseDeal = (data) => {
    createClosedDealMutation(data);
  };

  const { mutate: createClosedDealMutation } = useMutation({
    mutationFn: (data) => createClosedDeal(data),
    onSuccess: () => {
      toast.success('Deal closed successfully');
      setIsCloseDealModalOpen(false);
      setIsSelectLeadModalOpen(false);
      const closedLeadId = leadId?.id || leadId;
      setLeadId('');
      refetchDeals();
      if (closedLeadId) {
        navigateWithReturn(navigate, `/builder/deals/${closedLeadId}`, {
          returnTo: buildReturnToFromLocation(location),
          state: { status: 'Closed Won' }
        });
      }
    },
    onError: (error) => {
      toast.error(error.response.data.message || error?.message || 'Failed to close deal');
    }
  });

  // Function to save filters to localStorage
  const saveFiltersToStorage = (filters) => {
    localStorage.setItem('customerListFilters', JSON.stringify(filters));
  };

  // Function to clear specific filter
  const clearFilter = (filterType) => {
    if (filterType === 'project') {
      setProjectID(null);
      const newFilters = { ...activeFilters, project: null, projectId: null };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    } else if (filterType === 'status') {
      const newFilters = { ...activeFilters, status: 'All' };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
      setPage(1); // Reset to first page when clearing status filter
    }

    // Track filter clear
    mixpanel.track('Customer List Filter Cleared', {
      user_id: userId,
      filter_type: filterType,
      project_id: projectId,
      timestamp: new Date().toISOString()
    });
  };

  const handleLeadSelect = async (lead) => {
    setLeadId(lead);
    let leadData = leadsData?.data?.find((l) => l.id === Number(lead));
    try {
      // Full lead for UC unit prefill (picker payload may omit nested block ids)
      const full = await getLeadByID(lead);
      if (full) leadData = full;
    } catch (err) {
      console.error('Failed to load lead details for close deal:', err);
    }
    if (leadData?.underConsideration) {
      const blockId = leadData?.underConsideration?.unit?.block_id;
      const levelId = leadData?.underConsideration?.unit?.level_id;
      const unitId = leadData?.underConsideration?.unit?.id;
      const projectIdFromUc = leadData?.underConsideration?.unit?.project_id;
      setUcDetails({
        project: projectIdFromUc,
        block: blockId,
        level: levelId,
        unit: unitId
      });
    } else {
      setUcDetails({ project: null, block: null, level: null, unit: null });
    }
    setIsSelectLeadModalOpen(false);
    setIsCloseDealModalOpen(true);
  };

  ////////////////////////
  const leadOptions = [
    { value: '', label: 'Select Lead', disabled: true },
    ...(leadsData?.data
      ?.map((lead) => {
        const name = lead?.user?.name || 'Unnamed Lead';
        const hasUnderConsideration = lead?.underConsideration;
        const unitName = lead?.underConsideration?.unit?.unitName;
        const suffix = hasUnderConsideration && unitName ? ` (${unitName})` : '';
        return {
          value: lead.id,
          label: `${name}${suffix}`
        };
      }) || [])
  ];

  const { mutate: handleCancelDealFn } = useMutation({
    mutationFn: (payload) => cancelCloseDeal(payload),
    onMutate: () => {
      setIsSubmitting(true);
      mixpanel.track('Cancel Deal Started', {
        user_id: userId,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: (res, variables) => {
      setIsSubmitting(false);
      toast.success('Deal cancelled successfully');
      setShowCancelModal(false);
      mixpanel.track('Cancel Deal Success', {
        user_id: userId,
        lead_id: leadID,
        timestamp: new Date().toISOString()
      });
      // Optionally refresh data
      refetchDeals();
      reset();
    },
    onError: (err, variables) => {
      setIsSubmitting(false);
      toast.error(err?.response?.data?.message || err?.message || 'Error cancelling deal');
      mixpanel.track('Cancel Deal Failed', {
        user_id: userId,
        lead_id: leadID,
        error_message: err?.response?.data?.message || err?.message,
        timestamp: new Date().toISOString()
      });
    }
  });

  const openCancelDealModal = (id) => {
    const selectedDeal = deals?.data?.find((deal) => deal.id === parseInt(id.id));
    setLeadID(selectedDeal.lead_id);
    setSelectedDeal(selectedDeal);
    setValue('total_price', `₹${parseInt(selectedDeal.total_price).toLocaleString('en-IN')}` || 0);
    setValue('paidAmount', `₹${parseInt(selectedDeal.paidAmount).toLocaleString('en-IN')}` || 0);
    setValue('due_price', `₹${parseInt(selectedDeal.due_price).toLocaleString('en-IN')}` || 0);
    setValue(
      'totalLateFees',
      `₹${parseInt(selectedDeal.totalLateFees).toLocaleString('en-IN')}` || 0
    );
    setShowCancelModal(true);
  };

  const confirmCancelDeal = () => {
    if (!leadID) {
      toast.error('Lead not found for this deal');
      return;
    }

    // Validate mandatory fields
    const emdPercentage = watch('emd_percentage');
    const adjustedPenalty = watch('adjusted_penalty');

    if (!emdPercentage || emdPercentage < 0) {
      toast.error('Please enter EMD %');
      return;
    }

    if (!adjustedPenalty || adjustedPenalty < 0) {
      toast.error('Please enter Adjusted Penalty');
      return;
    }

    const payload = {
      name: 'Deal Cancelled',
      description: 'Deal Cancelled',
      lead_id: leadID,
      agreement_form: formatFiles(fileUrls.agreement_form),
      emd: parseFloat(watch('emd')?.replace(/[₹,]/g, '') || '0'),
      totalPenalty: parseFloat(watch('total_penalty')?.replace(/[₹,]/g, '') || '0'),
      adjustedPenalty: parseFloat(watch('adjusted_penalty')?.replace(/[₹,]/g, '') || '0'),
      finalPayable: parseFloat(watch('final_payable')?.replace(/[₹,]/g, '') || '0')
    };

    console.log('📤 Cancel Deal Payload:', payload);
    handleCancelDealFn(payload);
  };

  return (
    <div className="w-full flex flex-col bg-gray-100 pb-0 overflow-hidden mt-[4rem]">
      {/* select Lead Modal */}
      {isSelectLeadModalOpen && (
        <Modal
          width={50}
          name="Select Lead"
          modalType="select-lead"
          onClose={() => {
            setIsSelectLeadModalOpen(false);
            setLeadId('');
            setValue('lead_id', '');
          }}>
          <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
            <div className="flex justify-between items-center w-full">
              <FormInput
                width={100}
                label={'Lead'}
                register={register}
                register_key={'lead_id'}
                onDropdownChange={handleLeadSelect}
                onSearchChange={setLeadPickerSearch}
                type="dropdown-autofilter"
                options={leadOptions}
                errors={selectErrors.lead_id}
              />
            </div>
            <div className="flex justify-end items-center mt-4">
              <div className="space-x-4 flex">
                <Button
                  type="button"
                  onClick={() => {
                    setIsSelectLeadModalOpen(false);
                    setLeadId('');
                  }}
                  displayType="secondary">
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={isSubmitting || isLeadsLoading}
                  onClick={() => {
                    setIsSelectLeadModalOpen(false);
                    setIsCloseDealModalOpen(true);
                  }}>
                  Select
                </Button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {showCancelModal && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Cancel Deal Confirmation'}
          onClose={() => {
            reset();
            setShowCancelModal(false);
          }}
          width={55}>
          <div className="grid grid-cols-3 gap-4">
            <FormInput
              width={100}
              label={'Unit Price'}
              type={'text'}
              readOnly={true}
              register={register}
              register_key={'total_price'}
            />

            <FormInput
              width={100}
              register={register}
              register_key={`paidAmount`}
              label={'Paid Amount'}
              type={'number'}
              errors={selectErrors.paidAmount}
              readOnly={true}
            />
            <FormInput
              width={100}
              register={register}
              register_key={`due_price`}
              label={'Pending Amount'}
              type={'number'}
              errors={selectErrors.due_price}
              readOnly={true}
            />
            <FormInput
              width={100}
              register={register}
              register_key={`totalLateFees`}
              label={'Interest Levied on Pending Amount'}
              type={'number'}
              errors={selectErrors.totalLateFees}
              readOnly={true}
            />
            <FormInput
              width={100}
              register={register}
              register_key={`emd_percentage`}
              label={'EMD %'}
              type={'number'}
              errors={selectErrors.emd_percentage}
              readOnly={false}
              isRequired={true}
              onType={(value) => {
                const totalPrice = selectedDeal?.total_price || 0;
                const emdPercentage = parseFloat(value) || 0;
                const emdAmount = (totalPrice * emdPercentage) / 100;
                setValue('emd', `₹${parseInt(emdAmount).toLocaleString('en-IN')}`);

                // Recalculate total penalty
                const totalLateFees = selectedDeal?.totalLateFees || 0;
                const totalPenalty = emdAmount + totalLateFees;
                setValue('total_penalty', `₹${parseInt(totalPenalty).toLocaleString('en-IN')}`);
              }}
            />
            <FormInput
              width={100}
              register={register}
              register_key={`emd`}
              label={'EMD'}
              type={'number'}
              errors={selectErrors.emd}
              readOnly={true}
            />
            <FormInput
              width={100}
              register={register}
              register_key={`total_penalty`}
              label={'Total Penalty'}
              type={'number'}
              errors={selectErrors.total_penalty}
              readOnly={true}
            />
            <FormInput
              width={100}
              register={register}
              register_key={`adjusted_penalty`}
              label={'Adjusted Penalty'}
              type={'number'}
              errors={selectErrors.adjusted_penalty}
              readOnly={false}
              isRequired={true}
              onType={(value) => {
                const paidAmount = selectedDeal?.paidAmount || 0;
                const adjustedPenalty = parseFloat(value) || 0;
                const finalPayable = paidAmount - adjustedPenalty;
                setValue('final_payable', `₹${parseInt(finalPayable).toLocaleString('en-IN')}`);
              }}
            />
            <FormInput
              width={100}
              register={register}
              register_key={`final_payable`}
              label={'Final Payable'}
              type={'number'}
              errors={selectErrors.final_payable}
              readOnly={true}
            />
            <FormInput
              width={100}
              register={register}
              register_key={`agreement_form`}
              label={'Cancellation Agreement Form'}
              type={'file-btn'}
              prefilledFiles={fileUrls[`agreement_form`] || []}
              accept={'.pdf, .png, .jpg, .jpeg, .doc '}
            />
          </div>
          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="button"
              displayType="secondary"
              onClick={() => {
                setShowCancelModal(false);
                reset();
                setValue('agreement_form', []);
                dispatch(
                  uploadFile({
                    fileType: 'agreement_form',
                    url: []
                  })
                );
              }}>
              No
            </Button>
            <Button type="button" onClick={confirmCancelDeal} disabled={isSubmitting}>
              {isSubmitting ? 'Cancelling...' : 'Yes'}
            </Button>
          </div>
        </Modal>
      )}

      {/* Close Deal Modal */}
      {isCloseDealModalOpen && (
        <Modal
          width={50}
          name="Close Deal"
          confirmOnClose={true}
          isDirty={true}
          onClose={() => {
            setIsCloseDealModalOpen(false);
            setIsSelectLeadModalOpen(true);
          }}>
          <ClosedWonCloseDeal
            onClose={() => {
              setIsCloseDealModalOpen(false);
              setIsSelectLeadModalOpen(true);
            }}
            onSubmit={handleCloseDeal}
            isSubmitting={isSubmitting}
            selectedLead={leadId}
            ucDetails={ucDetails}
          />
        </Modal>
      )}

      {isUpdateModalOpen && (
        <Modal width={40} name="Update Sales User" onClose={() => setIsUpdateModalOpen(false)}>
          <UpdateSalesUser
            leadId={selectedLead?.id}
            currentSalesId={selectedLead?.salesId}
            projectId={projectId}
            onClose={() => setIsUpdateModalOpen(false)}
            onSuccess={() => {
              refetchDeals();
              setIsUpdateModalOpen(false);
            }}
          />
        </Modal>
      )}

      <Loader isVisible={isSubmitting} />
      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`Cannot delete '${resData?.name || 'this closed deals'}'`}
          onClose={() => {
            setCanDelete(null);
            setIsBulk(false);
            setSelectAll(false);
            setSelectedItems([]);
            setSelectedIds([]);
          }}
          width={45}>
          <div className="flex flex-col justify-center align-center ">
            <p className="text-center">
              Looks like this closed deals has dependencies, please delete following
              performainvoices and applications to proceed
            </p>
            <div className="flex justify-start, align-middle flex-wrap bg-[#f5f7f8] p-2 rounded-[5px] mt-4">
              {(() => {
                const reasons = Array.isArray(resData?.reason)
                  ? resData.reason
                  : resData?.reason
                    ? [resData.reason]
                    : [];
                return reasons.map((r, index) => (
                  <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                    {index + 1}.{r}
                    {index < reasons.length - 1 ? ', ' : ''}
                  </span>
                ));
              })()}
            </div>
          </div>
          <div className="flex flex-col justify-start items-center mt-4 w-[100%]  bg-[#f7f4d0] pt-[4px] pb-[4px] pl-2 pr-2 rounded-[4px]">
            <div className="flex justify-between w-[99%] items-center bg-white p-2 pt-1 pb-1">
              <p>Would you like to delete more closed deals ?</p>
              <div
                onClick={(e) => {
                  setIsBulk(!isBulk);
                  setSelectedItems([]);
                  setSelectAll(false);
                }}
                className="bg-amber-100 p-2 pt-1 pb-1 rounded-[5px] flex justify-center items-center gap-2 cursor-pointer">
                Bulk Delete
                <input
                  type="checkbox"
                  checked={isBulk}
                  readOnly
                  className="outline-none border-none"
                />
              </div>
            </div>

            {isBulk && (
              <div className="flex justify-end w-[100%] pr-[1.2rem] pt-3">
                <div className="flex gap-2">
                  <p>Select All: </p>
                  <input
                    className="outline-none border-none"
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </div>
              </div>
            )}
            {isBulk && (
              <div className="pl-[10px] w-[100%] pr-[19px] mt-2 mb-2 max-h-[14rem] overflow-auto">
                {filteredData &&
                  filteredData?.map((c, index) => (
                    <div key={index} className="flex justify-between items-center text-[#5b5b5b]">
                      {index + 1}. {c.name}
                      <input
                        className="outline-none border-none"
                        type="checkbox"
                        checked={selectedItems?.includes(c.id)}
                        disabled={selectedIds?.includes(c.id)} // Disable if already selected
                        onChange={(e) => HandleFunctionCalls(c.id, e.target.checked)}
                      />
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="reset"
              onClick={() => {
                setCanDelete(null);
                setIsBulk(false);
                setSelectAll(false);
                setSelectedItems([]);
                setSelectedIds([]);
              }}
              displayType="secondary">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                HandleDelete(selectedItems);
              }}
              disabled={isSubmitting || selectedItems.length === 0}
              displayType="primary-red">
              Delete
            </Button>
          </div>
        </Modal>
      )}

      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Confirmation'}
          onClose={() => {
            setCanDelete(null);
            setSelectedIds([]);
          }}
          width={45}>
          <DeleteConfirmationModal
            name={resData.name}
            title={'closed deals'}
            onClose={() => setCanDelete(null)}
            data={filteredData}
            type={'closedDeals'}
            setCanDelete={setCanDelete}
            onDeleteClick={HandleDelete}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
          />
        </Modal>
      )}

      <Headroom
        disable
        style={{
          position: 'fixed',
          top: '4.6rem',
          left: collapsed ? '76px' : '16.6%',
          right: '0',
          zIndex: 10,
          transition: 'all 0.3s ease-in-out'
        }}>
        <div className="flex items-center justify-between px-6 py-4 bg-white border-t-[2px] border-stone-100 shadow-sm transition-all duration-300 ease-in-out">
          <div className="flex flex-row gap-5 justify-start items-center">
            <Search
              onChange={handleSearchChange}
              query={searchQuery}
              onClick={handleOnClickSearch}
            />
            {/* Filter Indicator */}
            {activeFilters.project && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Filtering by:</span>
                <div className="flex items-center gap-1">
                  {activeFilters.project && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {activeFilters.project}
                      <button
                        onClick={() => clearFilter('project')}
                        className="ml-1 text-blue-600 hover:text-blue-800 font-bold text-sm">
                        ×
                      </button>
                    </span>
                  )}
                  {activeFilters.status && activeFilters.status !== 'All' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                      {activeFilters.status}
                      <button
                        onClick={() => clearFilter('status')}
                        className="ml-1 text-green-600 hover:text-green-800 font-bold text-sm">
                        ×
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-5 items-center">
            <Button
              onClick={() => {
                setIsSelectLeadModalOpen(true);
                setValue('lead_id', '');
              }}>
              Close Deal
            </Button>
          </div>
        </div>
      </Headroom>

      {/* Grid */}

      <GridWrapper width={100}>
        <div className="w-full overflow-clip bg-gray-100">
          <div className="overflow-scroll hide-scrollbar w-full relative rounded-t-lg mt-3">
            <table className="text-left w-full">
              <thead
                className="bg-gray-200 !text-gray-600 w-full"
                style={{ position: 'sticky', top: 0, left: 0, zIndex: 1 }}>
                <tr className="font-normal h-[3rem] w-full">
                  <th className="px-4 py-2 border-b font-normal">Lead Name</th>
                  <th className="px-4 py-2 border-b font-normal">Phone</th>
                  <th className="px-4 py-2 border-b font-normal" ref={projectHeaderRef}>
                    <div
                      className="flex items-center justify-between gap-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHeaderDropdownClick('project', e);
                      }}>
                      Project
                      <ChevronDown size={16} />
                    </div>

                    {openDropdown === 'project' && projectDropdownRect && (
                      <PortalDropdown
                        anchorRect={projectDropdownRect}
                        onClose={handleCloseDropdown}>
                        <div
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer bg-gray-100"
                          onClick={() => handleSelectDropdownItem('project', '')}>
                          <div className="flex items-center justify-between w-full">
                            <span>
                              {activeFilters.project
                                ? `Clear Filter (${activeFilters.project})`
                                : 'Select Project'}
                            </span>
                          </div>
                        </div>
                        {projectsForDropdown?.map((proj) => (
                          <div
                            key={proj.id}
                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${proj.id === projectID && 'bg-blue-100'
                              }`}
                            onClick={() => handleSelectDropdownItem('project', proj.id)}>
                            <div className="flex items-center justify-between w-full">
                              <span>{proj.label}</span>
                              {proj.id === projectID && (
                                <span className="text-xs text-blue-600 font-medium">Current</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </PortalDropdown>
                    )}
                  </th>
                  <th className="px-4 py-2 border-b font-normal">Unit Sold</th>
                  <th className="px-4 py-2 border-b font-normal">PLC Price</th>
                  <th className="px-4 py-2 border-b font-normal">Cost</th>
                  <th className="px-4 py-2 border-b font-normal">GST</th>
                  <th className="px-4 py-2 border-b font-normal">Total</th>
                  <th className="px-4 py-2 border-b font-normal" ref={statusHeaderRef}>
                    <div
                      className="flex items-center justify-between"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHeaderDropdownClick('status', e);
                      }}>
                      Status
                      <ChevronDown size={16} />
                    </div>
                    {openDropdown === 'status' && statusDropdownRect && (
                      <PortalDropdown anchorRect={statusDropdownRect} onClose={handleCloseDropdown}>
                        <div
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer bg-gray-100"
                          onClick={() => handleSelectDropdownItem('status', '')}>
                          <div className="flex items-center justify-between w-full">
                            <span>
                              {activeFilters.status && activeFilters.status !== 'All'
                                ? `Clear Filter (${activeFilters.status})`
                                : 'Select Status'}
                            </span>
                          </div>
                        </div>
                        {statusOptions
                          .filter((status) => status !== 'All')
                          .map((status, idx) => (
                            <div
                              key={status}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${status === activeFilters.status && 'bg-blue-100'
                                }`}
                              onClick={() => handleSelectDropdownItem('status', status)}>
                              <div className="flex items-center justify-between w-full">
                                <span>{status}</span>
                                {status === activeFilters.status && (
                                  <span className="text-xs text-green-600 font-medium">
                                    Current
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                      </PortalDropdown>
                    )}
                  </th>
                  <th className="px-4 py-2 border-b font-normal">Actions</th>
                </tr>
              </thead>

              <tbody>
                {Array.isArray(filteredData) && filteredData.length > 0 ? (
                  filteredData.map((item, idx) => (
                    <tr
                      key={item.id}
                      className="border-b hover:bg-gray-50 bg-white h-[3.5rem] cursor-pointer"
                      onClick={() => handleViewClosedDealsByID(item.id)}>
                      <td className="px-4 py-2 text-sm">{item.name || '---'}</td>
                      <td className="px-4 py-2 text-sm">{item.phone || '---'}</td>
                      <td className="px-4 py-2 text-sm">{item.project || '---'}</td>
                      <td className="px-4 py-2 text-sm">{item.unit_sold || '---'}</td>
                      <td className="px-4 py-2 text-sm">{item.Plc_Price || '---'}</td>
                      <td className="px-4 py-2 text-sm">{item.cost || '---'}</td>
                      <td className="px-4 py-2 text-sm">{item.GST || '---'}</td>
                      <td className="px-4 py-2 text-sm">{item.total || '---'}</td>
                      <td className="px-4 py-2 text-sm">
                        <TableData data={item.status || '---'} width={100} />
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <div className="flex items-center justify-start w-full relative gap-3">
                          {canAssignLead && (
                            <div
                              className="cursor-pointer hover:scale-110 transition-all duration-300 action-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdate(item.id);
                              }}>
                              <img src={editIcon} alt="edit__icon" />
                            </div>
                          )}
                          <div
                            className="cursor-pointer hover:scale-110 transition-all duration-300 action-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCheckDelete(item.id);
                            }}>
                            <img src={deleteIcon} alt="delete__icon" />
                          </div>
                          {userVertical === 'Management' && item.status !== 'Cancelled' && (
                            <div
                              className="cursor-pointer hover:scale-110 transition-all duration-300 action-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openCancelDealModal(item);
                              }}>
                              <img
                                className="w-4 h-4"
                                src={cancelDealIcon}
                                alt="cancel_deal__icon"
                              />
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-10 align-middle">
                      <div className="flex flex-col items-center justify-center w-full h-full">
                        <h2 className="text-lg font-bold text-gray-800 mb-1">No Data Found</h2>
                        <p className="text-gray-600 text-sm">
                          No customers found for the selected filters.
                          <br />
                          Try adjusting your search criteria.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {filteredData?.length > 0 && (
          <div className="flex justify-center items-center relative h-[10rem] w-full">
            <PaginationComponent
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              isFetching={isFetching}
            />
          </div>
        )}
      </GridWrapper>
    </div>
  );
};

export default CustomerList;
